import assignmentService from 'ApiService/Student/assignmentService';
import Card from 'components/card';
import TextField from 'components/fields/TextField';
import { AuthContext } from 'contexts/AuthContext';
import { useStepperNavigation } from 'pages/Hooks/useStepperKeyboardNavigation';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Stepper from 'views/admin/main/users/newUser/components/Stepper';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';
const MCQQuestion = ({ question, onAnswer, selectedAnswer }) => {
  return (
    <div className="mb-6">
      <h3 className="mb-3 text-lg font-semibold">{question.questionText}</h3>
      <div className="space-y-2">
        {question.options.map((option, index) => (
          <button
            key={index}
            className={`w-full rounded border p-2 text-left transition-colors ${
              selectedAnswer === option
                ? 'bg-brand-500 text-white'
                : 'hover:bg-gray-100 hover:dark:bg-navy-900'
            }`}
            onClick={() => onAnswer(question._id, option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

const QAQuestion = ({ question, onAnswer, answer }) => {
  return (
    <div className="mb-6">
      <h3 className="mb-3 text-lg font-semibold">{question.questionText}</h3>
      <TextField
        className="w-full rounded border p-2"
        rows="4"
        value={answer || ''}
        onChange={(e) => onAnswer(question._id, e.target.value)}
        placeholder="Type your answer here..."
      />
    </div>
  );
};

const StepperControl = ({
  steps,
  currentStep,
  handleClick,
  loading,
  disable,
  handleSubmit,
}) => {
  useStepperNavigation({
    currentStep,
    steps,
    handleClick,
    handleSubmit,
    disable,
  });

  return (
    <div className="mt-4 flex flex-col justify-between px-3 pt-2 md:flex-row md:px-8">
      {currentStep !== 1 && (
        <button
          onClick={() => handleClick('prev')}
          className={`mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20`}
        >
          Prev
        </button>
      )}
      <button
        disabled={disable || loading}
        onClick={() => {
          currentStep === steps.length ? handleSubmit() : handleClick('next');
        }}
        className="linear cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90 md:ml-2"
      >
        {loading
          ? 'Submitting...'
          : currentStep === steps.length
          ? 'Submit'
          : 'Next'}
      </button>
    </div>
  );
};

const Index = ({ handleBackClick, question }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const { state } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!question || !question.questions || question.questions.length === 0) {
      toast.error('No questions available');
      handleBackClick();
    }
  }, [question, handleBackClick]);

  const steps =
    question?.questions?.map((q, index) => ({
      stepNo: index + 1,
      name: `Question ${index + 1}`,
    })) || [];

  const handleAnswer = (questionId, answer) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const displayStep = (step) => {
    const questionIndex = step.stepNo - 1;
    const currentQuestion = question?.questions[questionIndex];

    if (!currentQuestion) return null;

    return question.type === 'mcq' ? (
      <MCQQuestion
        question={currentQuestion}
        onAnswer={handleAnswer}
        selectedAnswer={answers[currentQuestion._id]}
      />
    ) : (
      <QAQuestion
        question={currentQuestion}
        onAnswer={handleAnswer}
        answer={answers[currentQuestion._id]}
      />
    );
  };

  const handleClick = (direction) => {
    let newStep = currentStep;
    direction === 'next' ? newStep++ : newStep--;
    newStep > 0 && newStep <= steps.length && setCurrentStep(newStep);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formattedAnswers = Object.entries(answers).map(
        ([questionId, answer]) => ({
          questionId,
          answer,
        })
      );
      const payload = {
        AssignmentId: question._id,
        answers: formattedAnswers,
      };
      const response = await assignmentService.submitAssignmentAnswers(payload);

      if (response.data.success) {
        setSubmissionResult(response.data);
      } else {
        toast.error(response.data.message || 'Failed to submit test.');
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      toast.error(
        error.response?.data?.message ||
          'An error occurred while submitting the test. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (submissionResult) {
    const isMCQ = question.type === 'mcq';
    return (
      <div className="mt-3 h-full w-full">
        <div className="mx-auto h-full w-5/6 md:w-7/12">
          <Card extra={'h-full mx-auto pb-3'}>
            <div className="rounded-[20px] p-6 text-center">
              <h2 className="mb-6 text-2xl font-bold">{question.title}</h2>
              {isMCQ ? (
                <div className="my-8">
                  <p className="text-5xl font-bold text-brand-500">
                    {submissionResult.score}
                  </p>
                  <p className="mt-2 text-xl text-gray-500">
                    out of {submissionResult.totalQuestions}
                  </p>
                </div>
              ) : (
                <div className="my-8">
                  <div className="text-6xl">{'\u2705'}</div>
                  <p className="mt-4 text-xl font-semibold text-green-600">
                    Answers Submitted Successfully!
                  </p>
                  <p className="mt-2 text-gray-500">
                    Your answers are pending review by your teacher.
                  </p>
                </div>
              )}
              <div className="mt-6 flex justify-center gap-4">
                {isMCQ && (
                  <button
                    onClick={() => navigate('/student/view/assignment')}
                    className="rounded-xl bg-brand-900 px-8 py-3 font-medium text-white transition hover:bg-brand-800"
                  >
                    View Details
                  </button>
                )}
                <button
                  onClick={handleBackClick}
                  className="rounded-xl bg-lightPrimary px-8 py-3 font-medium text-navy-700 transition dark:bg-white/5 dark:text-white"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!question) return null;

  return (
    <div className="mt-3 h-full w-full">
      <button
        onClick={handleBackClick}
        className="mb-4 rounded-xl bg-brand-400 px-4 py-2 text-white"
      >
        Back to Dashboard
      </button>
      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />
      <div className="w-md:2/3 mx-auto h-full w-5/6 md:px-3 3xl:w-7/12">
        <div className="-mt-[280px] w-full pb-10 md:-mt-[340px] md:px-[70px]">
          <Stepper steps={steps} currentStep={currentStep} />
        </div>
        <Card extra={'h-full mx-auto pb-3'}>
          <div className="rounded-[20px] p-6">
            <h2 className="mb-6 text-2xl font-bold">{question.title}</h2>
            <UseContextProvider>
              {displayStep(steps[currentStep - 1])}
            </UseContextProvider>
          </div>
          <StepperControl
            handleClick={handleClick}
            currentStep={currentStep}
            steps={steps}
            handleSubmit={handleSubmit}
            loading={loading}
            disable={!answers[question.questions[currentStep - 1]?._id]}
          />
        </Card>
      </div>
    </div>
  );
};

export default Index;
